import {
  EventUpdate,
  EventEditModes,
  EventEditModeEnum,
  ShiftEventAndUpdateEvent,
  EventType,
  BaseEvent,
  Task,
  ParameterChange,
  isStoppageEventType,
  isPartialStoppage,
  isTask,
  isSpot,
  isParameterChange,
  isSystemChangeRequest
} from '@domain/shifthandover'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {useNavigationBlocker} from '@hconnect/common/hooks'
import {validateAll, ValidatorConfig} from '@hconnect/common/validators'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Paper} from '@mui/material'
import {ConfirmCloseDialog, EventTypeTag} from '@widgets/common-shift-event'
import {TFunction} from 'i18next'
import {isEqual} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {useTypeSpecificValidatorConfig} from '../hooks'
import {useTaskResolvedCommentDialog} from '../hooks/useTaskResolvedCommentDialog'
import {
  EventFormState,
  RepetitionInputsSettings,
  SharedEventStructure,
  ChecklistsInputSettings
} from '../types'
import {getEquipmentIdType} from '../utils'

import {TaskResolvedMandatoryCommentDialog} from './dialogs/TaskResolvedMandatoryCommentDialog'
import {
  EventFormActions,
  SharedEventForm,
  ParameterChangeForm,
  TaskForm,
  SystemChangeRequestForm
} from './eventForm'
import {SpotEventForm} from './eventForm/SpotForm'
import {StoppageFormWrapper} from './StoppageFormWrapper'

type Props = {
  plantId: string
  country: string
  item: Partial<BaseEvent>
  doClose(): void
  doSubmit(item: EventUpdate): void
  isCreateMode?: boolean
  editMode?: EventEditModes
  eventTypeLabelPrefix?: string
  children: React.ReactNode
  hideStoppageDateType?: boolean
  checklistsInputSettings?: ChecklistsInputSettings
  isEquipmentAvailable?: boolean
  skipOverlapValidation?: boolean
}

const isSeriesEventMode = (editMode?: EventEditModes) =>
  editMode === EventEditModeEnum.SINGLE_IN_SERIES || editMode === EventEditModeEnum.SERIES

const eventFormStateReducer = <K extends keyof BaseEvent>(
  state: EventFormState,
  validatorConfig: ValidatorConfig<ShiftEventAndUpdateEvent>,
  key: K,
  value: BaseEvent[K]
): EventFormState => {
  // do the change optimistically
  const next = {
    ...state,
    item: {
      ...state.item,
      [key]: value
    }
  }

  // validate the input if needed
  if (state.showValidation && validatorConfig.has(key)) {
    const failed = validatorConfig.get(key)?.(value, state.item)
    if (failed) {
      if (failed !== next.validationError.get(key)) {
        next.validationError = new Map(next.validationError)
        next.validationError.set(key, failed)
      }
    } else {
      if (next.validationError.has(key)) {
        next.validationError = new Map(next.validationError)
        next.validationError.delete(key)
      }
    }
  }

  return next
}

// helper to maintain a fix pointer for an empty validationError map
const emptyMap = new Map()

const generateRepetitionInputsSettings = (
  isSeriesEvent: boolean,
  item: Partial<BaseEvent>,
  t: TFunction,
  editMode?: EventEditModes
): RepetitionInputsSettings => ({
  disableCheckbox: isSeriesEvent || (isTask(item) && !!(item.doNotStartBefore && item.dueDate)),
  disableSelect: editMode === EventEditModeEnum.SINGLE_IN_SERIES,
  checkboxMessage:
    isTask(item) && !!(item.doNotStartBefore && item.dueDate)
      ? t('shiftEvent.label.recurringDisabled')
      : undefined
})

const normalizeSpotItem = (item: Partial<BaseEvent>) => {
  if (isSpot(item) && item.status === 'none') {
    return Object.fromEntries(
      Object.entries(item).filter(([key]) => key !== 'status')
    ) as Partial<BaseEvent>
  }

  return item
}

type RenderEventSpecificFormParams = {
  item: Partial<BaseEvent>
  state: EventFormState
  update: unknown
  updateMultiPart: (delta: Partial<BaseEvent>) => void
  children: React.ReactNode
  plantId: string
  hideStoppageDateType?: boolean
  isCreateMode?: boolean
  equipmentIdType: ReturnType<typeof getEquipmentIdType>
  autocompleteProcessStage?: boolean
  isEquipmentAvailable?: boolean
  allowCancellation: boolean
  editMode?: EventEditModes
  repetitionInputsSettings: RepetitionInputsSettings
  checklistsInputSettings: ChecklistsInputSettings
  isSeriesEvent: boolean
}

const renderEventSpecificForm = ({
  item,
  state,
  update,
  updateMultiPart,
  children,
  plantId,
  hideStoppageDateType,
  isCreateMode,
  equipmentIdType,
  autocompleteProcessStage,
  isEquipmentAvailable,
  allowCancellation,
  editMode,
  repetitionInputsSettings,
  checklistsInputSettings,
  isSeriesEvent
}: RenderEventSpecificFormParams) => {
  if (isPartialStoppage(item)) {
    return (
      <StoppageFormWrapper
        item={item}
        originalItem={state.originalItem}
        updateMultiPart={updateMultiPart}
        showValidation={state.showValidation}
        validationError={state.validationError}
        plantId={plantId}
        hideStoppageDateType={hideStoppageDateType}
        isCreateMode={isCreateMode}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
        isEquipmentAvailable={isEquipmentAvailable}
      >
        {children}
      </StoppageFormWrapper>
    )
  }

  if (isTask(item)) {
    return (
      <TaskForm
        plantId={plantId}
        allowCancellation={allowCancellation}
        editWithTaskSchedules={editMode === EventEditModeEnum.SERIES}
        repetitionInputsSettings={repetitionInputsSettings}
        checklistsInputSettings={checklistsInputSettings}
        disableDoNotStartBefore={isSeriesEvent}
        item={item}
        update={update as never}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
        originalAssignees={(state.originalItem as Task).assignees}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
      >
        {children}
      </TaskForm>
    )
  }

  if (isParameterChange(item)) {
    return (
      <ParameterChangeForm
        plantId={plantId}
        isEditMode={!!editMode}
        item={item}
        update={update as never}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
        originalApprovers={(state.originalItem as ParameterChange).approvers || []}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
      >
        {children}
      </ParameterChangeForm>
    )
  }

  if (isSystemChangeRequest(item)) {
    return (
      <SystemChangeRequestForm
        plantId={plantId}
        isEditMode={!!editMode}
        item={item}
        update={update as never}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
      >
        {children}
      </SystemChangeRequestForm>
    )
  }

  if (isSpot(item)) {
    return (
      <SpotEventForm
        plantId={plantId}
        item={item}
        update={update as never}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
        equipmentIdType={equipmentIdType}
        autocompleteProcessStage={autocompleteProcessStage}
      >
        {children}
      </SpotEventForm>
    )
  }

  return (
    <SharedEventForm
      plantId={plantId}
      isEditMode={!!editMode}
      item={item as SharedEventStructure}
      update={update as never}
      updateMultiPart={updateMultiPart}
      validationError={state.showValidation ? state.validationError : emptyMap}
      equipmentIdType={equipmentIdType}
      autocompleteProcessStage={autocompleteProcessStage}
    >
      {children}
    </SharedEventForm>
  )
}

const FormWrapper: React.FC<{
  eventType?: EventType
  eventTypeLabelPrefix?: string
  children: React.ReactNode
}> = ({children, eventType, eventTypeLabelPrefix}) => {
  if (isStoppageEventType(eventType)) {
    return (
      <ContentBox
        data-test-id="event-form"
        bodyWithPadding
        title={
          eventType && (
            <EventTypeTag
              eventType={eventType}
              eventTypeLabelPrefix={eventTypeLabelPrefix}
              data-test-id="event-form-container-event-type-label"
            />
          )
        }
        mode="max100PercentOfParentHeight"
      >
        {children}
      </ContentBox>
    )
  }
  return (
    <Paper
      data-test-id="event-form"
      sx={{
        maxHeight: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        p: 3,
        flexShrink: 1
      }}
    >
      {children}
    </Paper>
  )
}

/**
 * Container component for the Event form, including validation and save button
 *
 * TODO the props.item will tell us what type of event to expect
 * so in theories we could derive the type of update functions and the type of the state from the type of the item
 * and could get rid to the types casts in this component
 */
export const EventFormContainer: React.FC<Props> = ({
  plantId,
  country,
  item: originalItem,
  doClose,
  doSubmit: parentDoSubmit,
  editMode,
  eventTypeLabelPrefix,
  hideStoppageDateType,
  isCreateMode,
  checklistsInputSettings = {disableSelect: false, showChecklists: false},
  children,
  isEquipmentAvailable,
  skipOverlapValidation
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const shouldShowExtendedEquipmentId = useCommonFeatureFlag(FeatureFlagKey.ExtendedEquipmentIds, {
    plantId
  })
  const equipmentIdType = getEquipmentIdType(shouldShowExtendedEquipmentId ?? false)
  const autocompleteProcessStage = useCommonFeatureFlag(FeatureFlagKey.AutocompleteProcessStage, {
    plantId
  })
  const isFunctionalLocationOptional = useCommonFeatureFlag(
    FeatureFlagKey.FunctionalLocationOptional,
    {
      plantId
    }
  )
  const isOverlapValidationEnabled = useCommonFeatureFlag(FeatureFlagKey.StoppageOverlapValidation)

  // Normalize Spot events: remove invalid 'none' status
  const normalizedItem = useMemo(() => normalizeSpotItem(originalItem), [originalItem])

  // state setup
  const [state, setState] = useState<EventFormState>({
    originalItem: normalizedItem,
    item: normalizedItem,
    showValidation: false,
    validationError: emptyMap
  })

  useEffect(() => {
    if (!isEqual(normalizedItem, state.originalItem)) {
      setState({
        originalItem: normalizedItem,
        item: normalizedItem,
        showValidation: false,
        validationError: emptyMap
      })
    }
  }, [normalizedItem, state.originalItem])

  // variables
  const item = state.item

  const {
    isMandatoryCommentDialog,
    submitWithMandatoryCommentCheck,
    closeMandatoryCommentDialog,
    submitResolvedComment
  } = useTaskResolvedCommentDialog({
    originalItem,
    item,
    parentDoSubmit
  })

  const validatorConfig = useTypeSpecificValidatorConfig(
    plantId,
    item,
    isEquipmentAvailable && !isFunctionalLocationOptional,
    skipOverlapValidation || !isOverlapValidationEnabled
  )

  useEffect(() => {
    setState((currentState: EventFormState) => {
      if (!currentState.showValidation) return currentState
      const validationError = validateAll<ShiftEventAndUpdateEvent>(
        validatorConfig,
        currentState.item
      )
      return {
        ...currentState,
        validationError
      }
    })
  }, [validatorConfig])

  // hooks
  const {setIsBlockActive, isBlocked, handleClose} = useNavigationBlocker(
    !isEqual(item, originalItem)
  )

  const isSeriesEvent = isSeriesEventMode(editMode)

  const repetitionInputsSettings: RepetitionInputsSettings = useMemo(
    () => generateRepetitionInputsSettings(isSeriesEvent, item, t, editMode),
    [editMode, isSeriesEvent, item, t]
  )

  const allowCancellation = !!editMode

  const update = useCallback(
    (key, value) => {
      setState((currentState: EventFormState) => {
        if (currentState.item[key] === value) {
          // ignore updates that don't change anything, to prevent endless loops
          return currentState
        }
        return eventFormStateReducer(currentState, validatorConfig, key, value)
      })
    },
    [setState, validatorConfig]
  )

  const updateMultiPart = useCallback(
    (delta: Partial<BaseEvent>) => {
      setState((currentState: EventFormState) => {
        const nextItem = {
          ...currentState.item,
          ...delta
        } as BaseEvent

        const validationError = currentState.showValidation
          ? validateAll<ShiftEventAndUpdateEvent>(validatorConfig, nextItem) // TODO instead of validate all, just validate the "keyof delta"
          : currentState.validationError

        return {
          ...currentState,
          item: nextItem,
          validationError
        }
      })
    },
    [setState, validatorConfig]
  )

  return (
    <FormWrapper eventType={item.eventType} eventTypeLabelPrefix={eventTypeLabelPrefix}>
      {renderEventSpecificForm({
        item,
        state,
        update,
        updateMultiPart,
        children,
        plantId,
        hideStoppageDateType,
        isCreateMode,
        equipmentIdType,
        autocompleteProcessStage,
        isEquipmentAvailable,
        allowCancellation,
        editMode,
        repetitionInputsSettings,
        checklistsInputSettings,
        isSeriesEvent
      })}
      <EventFormActions
        plantId={plantId}
        country={country}
        state={state}
        setState={setState}
        doClose={doClose}
        parentDoSubmit={() => submitWithMandatoryCommentCheck(state.item as EventUpdate)}
        validatorConfig={validatorConfig}
      />
      {isMandatoryCommentDialog && (
        <TaskResolvedMandatoryCommentDialog
          open={isMandatoryCommentDialog}
          onClose={closeMandatoryCommentDialog}
          onSubmit={submitResolvedComment}
        />
      )}
      <ConfirmCloseDialog
        open={isBlocked}
        onCancel={handleClose}
        onConfirm={() => {
          handleClose()
          setIsBlockActive(false)
        }}
      />
    </FormWrapper>
  )
}
